import type { MaybeRefOrGetter } from 'vue';

import { defineQuery, useQuery, useQueryCache } from '@pinia/colada';
import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';

import type { QueueSettings } from '@/apis/scheduler/queue-settings';

import { getQueueSettings } from '@/apis/scheduler/queue-settings';
import { MUTABLE_QUERY_OPTIONS, QueryKeys } from '@/shared/queries';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';

export const useQueueSettings = defineQuery(() => {
  const { currentBrand } = storeToRefs(useIdentityStore());
  const brandId = computed(() => currentBrand.value?.id);

  const flagStore = useFlagStore();
  const autopilotEnabled = computed(
    () => flagStore.ready && !!flagStore.flags.schedulerPublishingAutopilot,
  );

  const { data, ...query } = useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.scheduler.queueSettings(brandId.value),
    query: async ({ signal }) => {
      const response = await getQueueSettings({ brandId: brandId.value! }, { signal });
      return response.data;
    },
    enabled: () => !!brandId.value && autopilotEnabled.value,
  });

  return {
    brandId,
    queueSettings: computed<QueueSettings | undefined>(() => data.value),
    queuePaused: computed<boolean>(() => data.value?.queuePaused ?? false),
    ...query,
  };
});

export function useQueueSettingsForBrands(brandIds: MaybeRefOrGetter<number[]>) {
  const flagStore = useFlagStore();
  const autopilotEnabled = computed(
    () => flagStore.ready && !!flagStore.flags.schedulerPublishingAutopilot,
  );

  const ids = computed(() => [...new Set(toValue(brandIds))].sort((a, b) => a - b));

  const { data, ...query } = useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.scheduler.queueSettingsForBrands(ids.value),
    query: async ({ signal }) => {
      const responses = await Promise.all(
        ids.value.map((brandId) => getQueueSettings({ brandId }, { signal })),
      );
      return responses.map((response) => response.data);
    },
    enabled: () => autopilotEnabled.value && ids.value.length > 0,
  });

  const pausedBrandIds = computed<number[]>(
    () =>
      data.value?.filter((settings) => settings.queuePaused).map((settings) => settings.brandId) ??
      [],
  );

  return {
    pausedBrandIds,
    anyQueuePaused: computed(() => pausedBrandIds.value.length > 0),
    ...query,
  };
}

/** Drops the freshness window so every active queue-settings query refetches immediately. */
export function invalidateQueueSettings(queryCache: ReturnType<typeof useQueryCache>) {
  void queryCache.invalidateQueries({ key: ['scheduler', 'queue-settings'] });
  void queryCache.invalidateQueries({ key: QueryKeys.scheduler.queueSettingsForBrands([]) });
}

/** invalidateQueueSettings as a zero-arg navigation guard / lifecycle callback. */
export function invalidateQueueSettingsGuard() {
  invalidateQueueSettings(useQueryCache());
}
